import React, {FC, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useQuery} from 'react-query'
import {subDays} from 'date-fns'
import {ValueType} from 'rsuite/cjs/DateRangePicker/types'
import {PageTitle} from '../../../_metronic/layout/core'
import {ChartsWidget7, ChartsWidget8} from '../../../_metronic/partials/widgets'
import {DateRange, PageHeader, usePeriod, UseWindowSize} from '../../modules/all-pages'
import {Get} from '../../modules/functions/get/Get'
import ChartDashboard1 from './components/ChartDashboard1'
import {Announcement} from './components/Announcement'
import {ChartDashboard4} from './components/ChartDashboard4'
import {ContactsDashboard} from './components/ContactsDashboard'
import {ReviewsSlider} from './components/reviews'
import {DashboardWrapperTypes} from './ts/types-new'

const DASHBOARD_WRAPPER = process.env.REACT_APP_V2_DASHBOARD_WRAPPER || 'v2/dashboard'

const styles = {
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gridTemplateRows: '1fr',
        gridGap: 10,
    },
    gridMd: {
        gridTemplateColumns: 'repeat(2, 1fr)',
    },
    gridSm: {
        gridTemplateColumns: 'repeat(2, 1fr)',
    },
}

const emptyChartOptions = {
    series: [],
    categoriesXAxis: [],
}

const DashboardPage: FC = () => {
    const [rangeValue, setRangeValue] = React.useState<ValueType>([subDays(new Date(), 29), new Date()])
    const [loadingData, setLoadingData] = useState<'idle' | 'pending' | 'fulfilled' | 'rejected'>('idle')
    const [dateTimeStart, dateTimeEnd] = rangeValue

    const period = usePeriod(dateTimeStart, dateTimeEnd)

    const {data, isFetching} = useQuery(
        ['dashboard_wrapper', period],
        () => Get<DashboardWrapperTypes>({endpoint: DASHBOARD_WRAPPER, period}),
        {
            enabled: !!period,
            onSuccess: (response) => {
                setLoadingData(response ? 'fulfilled' : 'rejected')
            },
            onError: () => {
                setLoadingData('rejected')
            },
        },
    )

    useEffect(() => {
        if (isFetching) {
            setLoadingData('pending')
        }
    }, [isFetching])

    const {innerWidth} = UseWindowSize()
    const chartOptions = data?.dashboardNumberOfVisits.chartOptions ?? emptyChartOptions
    const contacts = data?.dashboardLastContacts ?? []
    const announcement = data?.dashboardAnnouncement?.annonce_homepage_1 ?? null
    const visits = Number(data?.dashboardCards.visits ?? 0)
    const conversions = data?.dashboardCards.conversions ?? 0
    const forms = data?.dashboardCards.forms ?? 0
    const calls = Number(data?.dashboardCards.calls ?? 0)
    const clicksNumber = Number(data?.dashboardCards.numberOofClicksOnPhone ?? 0)

    const getResponsiveStyles = () => {
        if (innerWidth <= 480) {
            return {...styles.grid, ...styles.gridSm}
        } else if (innerWidth <= 768) {
            return {...styles.grid, ...styles.gridMd}
        } else {
            return styles.grid
        }
    }

    return (
        <>
            <PageHeader icon={'/media/icons/duotune/art/art002.svg'}
                        title={'Activite et performances de votre site web'}
                        description={"Ce tableau de bord vous offre une vue claire et concise de l'activite de votre site sur la periode selectionnee. Suivez vos indicateurs cles comme les visites, les conversions, les formulaires soumis et les appels telephoniques"}
            />

            <div className="row mb-8">
                <div className="col-12 d-flex justify-content-end align-items-center">
                    <DateRange rangeValue={rangeValue}
                               setRangeValue={setRangeValue}
                               loadingStatus={loadingData}
                               setLoadingStatus={(status) => setLoadingData(status)}
                               className="d-flex justify-content-end align-items-center"
                    />
                </div>
            </div>

            <div className="mb-5" style={getResponsiveStyles()}>
                <div>
                    <ChartDashboard1 bg={0}
                                     iconPath={'/media/icons/duotune/general/gen060.svg'}
                                     body={{total: visits, text: 'Visites'}}
                                     to={'/statistiques/s-seo'}
                    />
                </div>
                <div>
                    <ChartDashboard1 bg={1}
                                     iconPath={'/media/icons/duotune/arrows/arr029.svg'}
                                     body={{total: conversions, text: 'Conversions'}}
                    />
                </div>
                <div>
                    <ChartDashboard1 bg={0}
                                     iconPath={'/media/icons/duotune/general/gen005.svg'}
                                     body={{total: forms, text: 'Formulaires'}}
                                     to={'/web-formulaires/tableau-de-bord-wf'}
                    />
                </div>
                <div>
                    <ChartDashboard1 bg={1}
                                     iconPath={'/media/icons/duotune/communication/com014.svg'}
                                     body={{total: calls, text: 'Appels'}}
                                     to={'/call-tracking/journal-appels'}
                    />
                </div>
                <div>
                    <ChartDashboard1 bg={0}
                                     iconPath={'/media/icons/duotune/coding/cod006.svg'}
                                     body={{
                                         total: clicksNumber,
                                         text: 'Nombre de clics sur le telephone',
                                     }}
                                     to={'/clicks'}
                    />
                </div>
            </div>

            <div className="row d-flex justify-content-center">
                <div className="col-xl-4 card card-xl-stretch">
                    <ContactsDashboard contacts={contacts} />
                </div>
                <div className="col-xl-8">
                    <ChartDashboard4 className={'card-xl-stretch'}
                                     chartOptions={chartOptions}
                    />
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <Announcement announcement={announcement} />
                </div>
            </div>

            <div className="row my-5">
                <div className="col-12">
                    <ReviewsSlider data={data?.reviews_google} />
                </div>
            </div>

            <div className="row gy-5 g-xl-8">
                <div className="col-xl-6">
                    <ChartsWidget7 className={'card-xl-stretch'} />
                </div>
                <div className="col-xl-6">
                    <ChartsWidget8 className={'card-xl-stretch'} />
                </div>
            </div>
        </>
    )
}

const DashboardWrapper: FC = () => {
    const intl = useIntl()

    return (
        <>
            <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.DASHBOARD'})}</PageTitle>
            <DashboardPage />
        </>
    )
}

export {DashboardWrapper}
